import { renewAccessToken } from './tokenBridge';
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  // Same-origin by default in production builds — the UI and API share
  // ada.containerlabs.io behind one host (no CORS, cookies just work).
  // Dev server has no API process, so fall back to the local backend.
  return process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
};


async function parseBody(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function adaFetch(path, { method = 'GET', body, token } = {}) {
  const doFetch = (authToken) =>
    fetch(`${getApiUrl()}${path}`, {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

  let res = await doFetch(token);
  // Access token expired while idle: renew once via the refresh cookie and
  // replay the request transparently so the user's action just works.
  if (res.status === 401 && path !== '/login/google') {
    const fresh = await renewAccessToken();
    if (fresh && fresh !== token) {
      res = await doFetch(fresh);
    }
  }
  const data = await parseBody(res);
  if (!res.ok) {
    const detail =
      (data &&
        (data.detail ||
          (typeof data.error === 'string' ? data.error : null) ||
          data.message)) ||
      (typeof data === 'string' ? data : null) ||
      res.statusText;
    const err = new Error(detail);
    err.status = res.status;
    const retryAfter = res.headers && res.headers.get && res.headers.get('Retry-After');
    if (retryAfter) err.retryAfter = retryAfter;
    throw err;
  }
  return data;
}

export function listChats(token) {
  return adaFetch('/chats', { token });
}

export function createChat({ name, model }, token) {
  return adaFetch('/chats', {
    method: 'POST',
    token,
    body: { name, ...(model ? { model } : {}) },
  });
}

export function getChat(chatId, token) {
  return adaFetch(`/chats/${chatId}`, { token });
}

export function listMessages(chatId, token) {
  return adaFetch(`/chats/${chatId}/messages`, { token });
}

export function listModels(token) {
  return adaFetch('/models', { token });
}

export function getUsageReport(options, token) {
  return adaFetch(`/usage/report?${new URLSearchParams(options)}`, { token });
}

/** Today's workspace pulse — counts only (prompts/tokens/harness runs). */
export function getPulse(token) {
  return adaFetch('/usage/pulse', { token });
}

export function getMyUsage(days, token) {
  return adaFetch(`/usage/me?days=${days}`, { token });
}

export function getOrgUsage(days, token) {
  return adaFetch(`/usage/org?days=${days}`, { token });
}

/** Per-request dump in fin aiusage.Log shape. */
export function getUsageExport(days, token) {
  const q = days ? `?days=${days}` : '';
  return adaFetch(`/usage/export${q}`, { token });
}

export function submitSurface(chatId, surfaceId, data, token) {
  return adaFetch(`/chats/${chatId}/surfaces/${surfaceId}/submit`, {
    method: 'POST',
    token,
    body: { data: data || {} },
  });
}

export function cancelSurface(chatId, surfaceId, token) {
  return adaFetch(`/chats/${chatId}/surfaces/${surfaceId}/cancel`, {
    method: 'POST',
    token,
    body: {},
  });
}

export function listConnectors(token) {
  return adaFetch('/connectors', { token });
}

export function getConnector(id, token) {
  return adaFetch(`/connectors/${id}`, { token });
}

export function startConnector({ name, type, returnTo }, token) {
  return adaFetch('/connectors', {
    method: 'POST',
    token,
    body: {
      name,
      type: type || 'gmail',
      return_to: returnTo,
    },
  });
}

export function completeConnector({ code, state }, token) {
  return adaFetch('/connectors/complete', {
    method: 'POST',
    token,
    body: { code, state },
  });
}

export function mintConnectorToken(id, token) {
  return adaFetch(`/connectors/${id}/token`, {
    method: 'POST',
    token,
    body: {},
  });
}

export function deleteConnector(id, token) {
  return adaFetch(`/connectors/${id}`, {
    method: 'DELETE',
    token,
  });
}

export function generateWorld(body, token) {
  return adaFetch('/v1/worlds/generate', {
    method: 'POST',
    token,
    body,
  });
}

export function getWorldOperation(id, token) {
  return adaFetch(`/v1/worlds/operations/${id}`, { token });
}

export function getWorld(id, token) {
  return adaFetch(`/v1/worlds/${id}`, { token });
}

export function listWorlds(token) {
  return adaFetch('/v1/worlds', { token });
}

export function getWorldCredits(token) {
  return adaFetch('/v1/worlds/credits', { token });
}

export function connectorOAuthMeta(token) {
  return adaFetch('/connectors/oauth', { token });
}

export function promptMessage({ prompt, chatId, model, system, attachments, reasoning_effort, media }, token) {
  return adaFetch('/messages/prompt', {
    method: 'POST',
    token,
    body: {
      prompt,
      chatId,
      ...(model ? { model } : {}),
      ...(system ? { system } : {}),
        ...(attachments?.length ? { attachments } : {}),
        ...(reasoning_effort ? { reasoning_effort } : {}),
        ...(media ? { media } : {}),
    },
  });
}

/**
 * Consume /messages/stream SSE. Backend currently emits one content event then done.
 * onChunk({ content, done, response, ... }) for each event.
 */
export async function streamPrompt(
  { prompt, chatId, model, system, attachments, reasoning_effort, media },
  token,
  { onChunk, signal } = {}
) {
  const doFetch = (authToken) =>
    fetch(`${getApiUrl()}/messages/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({
        prompt,
        chatId,
        ...(model ? { model } : {}),
        ...(system ? { system } : {}),
        ...(attachments?.length ? { attachments } : {}),
        ...(reasoning_effort ? { reasoning_effort } : {}),
        ...(media ? { media } : {}),
      }),
      signal,
    });
  let res = await doFetch(token);
  // Same idle-expiry path as adaFetch: one silent renew + replay. The
  // replay only happens before the stream opens, so nothing is doubled.
  if (res.status === 401 && !signal?.aborted) {
    const fresh = await renewAccessToken();
    if (fresh && fresh !== token) {
      res = await doFetch(fresh);
    }
  }
  if (!res.ok) {
    const text = await res.text();
    let detail = res.statusText;
    try {
      detail = JSON.parse(text)?.detail || detail;
    } catch {
      if (text) detail = text;
    }
    throw new Error(detail);
  }
  const reader = res.body?.getReader();
  if (!reader) throw new Error('Streaming unsupported in this browser');
  const decoder = new TextDecoder();
  let buffer = '';
  let assembled = '';
  let finalPayload = null;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part
        .split('\n')
        .find((l) => l.startsWith('data: '));
      if (!line) continue;
      let data;
      try {
        data = JSON.parse(line.slice(6));
      } catch {
        continue;
      }
      if (typeof data.content === 'string') {
        assembled += data.content;
      }
      if (data.done || data.type === 'done') finalPayload = data;
      onChunk?.({ ...data, assembled });
    }
  }
  return { assembled, response: finalPayload?.response || null, final: finalPayload };
}

export function chatTitleFromPrompt(prompt, max = 48) {
  const cleaned = (prompt || '').trim().replace(/\s+/g, ' ');
  if (!cleaned) return 'New chat';
  return cleaned.length > max ? `${cleaned.slice(0, max - 1)}…` : cleaned;
}

export function getSafetySettings(token) {
  return adaFetch('/safety/settings', { token });
}

export function updateSafetySettings({ t1_enabled, t2_enabled }, token) {
  return adaFetch('/safety/settings', {
    method: 'PUT',
    token,
    body: { t1_enabled, t2_enabled },
  });
}

/** Gateway completion — stateless, used for meta-tasks (auto-titles). */
export function gatewayComplete(messages, { model = 'opencode/big-pickle' } = {}, token) {
  return adaFetch('/v1/chat/completions', {
    method: 'POST',
    token,
    body: { model, messages },
  });
}

/** History overlay search: q optional (empty = most recent). */
export function searchHistory(q, token) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  return adaFetch(`/history${qs}`, { token });
}

/** Rename a chat (agent auto-title or manual). */
export function updateChatName(chatId, name, token) {
  return adaFetch(`/chats/${chatId}`, {
    method: 'PUT',
    token,
    body: { name },
  });
}

export const getModelPreferences = (token) => adaFetch('/models/preferences', { token });
export const putModelPreferences = (preferences, token) => adaFetch('/models/preferences', {method:'PUT',body:preferences,token});

export const getAppearancePreferences = (token) => adaFetch('/v1/user/appearance', { token });
export const putAppearancePreferences = (preferences, token) => adaFetch('/v1/user/appearance', { method: 'PUT', body: preferences, token });
